import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fcfa, shortDate } from "../lib/format";
import { RequestDialog } from "../components/RequestDialog";
import { Plus, Wrench, Loader2 } from "lucide-react";

const STATUSES = ["ALL", "OPEN", "IN_PROGRESS", "COMPLETED", "CANCELLED"];

export default function Requests() {
  const { user } = useAuth();
  const isAdmin = ["admin", "manager"].includes(user.role);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/maintenance-requests").then((res) => setRequests(res.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "ALL" ? requests : requests.filter((r) => r.status === filter);

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-accent">Maintenance</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">{isAdmin ? "All Requests" : "My Requests"}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="requests-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          {!isAdmin && (
            <Link to="/requests/new"><Button className="rounded-full hover:bg-primary/90" data-testid="new-request-cta">
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" /> New</Button></Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Wrench className="h-10 w-10 mx-auto text-muted-foreground mb-4" aria-hidden="true" />
          <p className="font-heading text-lg font-bold">No requests</p>
          <p className="text-muted-foreground text-sm mt-1">Nothing here for this filter.</p>
        </Card>
      ) : (
        <Card className="border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="hidden md:table-cell">Description</TableHead>
                {isAdmin && <TableHead className="hidden lg:table-cell">Customer</TableHead>}
                <TableHead>Priority</TableHead>
                <TableHead className="hidden sm:table-cell">Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setActive(r)} data-testid={`request-row-${r.id}`}>
                  <TableCell className="font-medium">{r.category}</TableCell>
                  <TableCell className="hidden md:table-cell max-w-xs truncate text-muted-foreground">{r.description}</TableCell>
                  {isAdmin && <TableCell className="hidden lg:table-cell text-muted-foreground">{r.customer_name}</TableCell>}
                  <TableCell><span className="text-xs font-medium">{r.priority}</span></TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">{shortDate(r.created_at)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm">Open</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <RequestDialog request={active} open={!!active} onClose={() => setActive(null)} isAdmin={isAdmin} onUpdated={load} />
    </div>
  );
}
